/**
 * The page to go back to after a detour — picking a unit, signing in — when
 * the page asked for is a link from an email: a vote's email links to the
 * vote (VOTE-02 CA-02, VOTE-05 CA-03), not to the portal's home.
 *
 * Kept per tab in sessionStorage, and only ever a path of the app's votes,
 * owner or consent sections: anything else, another origin included, is
 * dropped, so a crafted link cannot send the reader somewhere else.
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';

/** Kept by the unit guard, followed by the unit picker. */
const RETURN_PATH_KEY = 'return_path';

/**
 * Kept while signed out, followed on signing in. A key of its own: the router
 * never follows the one the unit guard keeps, or the two send an owner of
 * several units back and forth between the vote and the unit picker.
 */
export const SIGN_IN_RETURN_PATH_KEY = 'sign_in_return_path';

const RETURN_PATH_PREFIXES = ['/votes/', '/owner/', '/consent/'];

export function isReturnPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && RETURN_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

export function saveReturnPath(path: string, key = RETURN_PATH_KEY): void {
  if (!isReturnPath(path)) {
    return;
  }
  try {
    sessionStorage.setItem(key, path);
  } catch {
    // sessionStorage unavailable — the detour ends on the home page, as before
  }
}

/** The saved path, once: reading it clears it. */
export function takeReturnPath(key = RETURN_PATH_KEY): string | null {
  try {
    const path = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    return isReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

/**
 * Keeps the page asked for while signed out (the landing page stands in for
 * it) and goes there once, when the session arrives. The Google sign-in comes
 * back on a full page load, so the app follows the path rather than the login
 * form alone. Only on that step: never on a later change of page.
 */
export function useReturnPath(isLoading: boolean, isAuthenticated: boolean): void {
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      saveReturnPath(location, SIGN_IN_RETURN_PATH_KEY);
    }
  }, [isLoading, isAuthenticated, location]);

  // Replaces the page the sign-in left him on: the login form may have gone
  // to the same path already.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const path = takeReturnPath(SIGN_IN_RETURN_PATH_KEY);
    if (path) {
      setLocation(path, { replace: true });
    }
  }, [isAuthenticated, setLocation]);
}
